import _en from './translations/en.json.mjs';
import React__default from 'react';
import classNames from 'classnames';
import { useI18n } from '@shopify/react-i18n';
import { UnstyledButton, Icon, Text, Spinner } from '@shopify/polaris';
import { ExternalSmallIcon, ChevronDownIcon, ChevronUpIcon, CaretUpIcon, CaretDownIcon, SelectIcon } from '@shopify/polaris-icons';
import { variationName } from '../../utilities/css.mjs';
import { Tone, AriaRole } from '../../types.mjs';
import { IS_PROD_ENV } from '../../utilities/env.mjs';
import { PlainActionDisclosureValue } from './types.mjs';
import styles from './PlainAction.scss.mjs';
import ChevronDownTiny from './icons/ChevronDownTiny.svg.mjs';
import { useCommonAction } from '../AppProvider/hooks/useCommonAction/useCommonAction.mjs';
import { CommonActionTypeValue } from '../AppProvider/hooks/useCommonAction/types.mjs';

// Polaris UnstyledButton has a loose interface that allows to pass down
// any prop to the underlying button element. However, we want to prevent
// the consumer from passing down invalid props to PlainAction, so instead
// of making PlainActionProps extend UnstyledButtonProps, we only pick valid
// HTML button props, and omit the ones that are already handled by Polaris.
const errorLabels = {
  noProps: 'You did not provide a useable `content`, `icon`, or `disclosure` prop',
  accessibleLabel: 'You did not provide an `accessibilityLabel` or an `ariaLabelledBy` to an icon-only variant'
};

// Required above PlainAction due to a Babel hoisting issue
// https://github.com/babel/babel/issues/10878
function LoadingIndicator() {
  const {
    content,
    accessibilityLabel
  } = useCommonAction({
    type: CommonActionTypeValue.Loading
  });
  return /*#__PURE__*/React__default.createElement("span", {
    className: styles.LoadingIndicator
  }, /*#__PURE__*/React__default.createElement(Text, {
    variant: "bodySm",
    as: "span",
    visuallyHidden: true
  }, content), /*#__PURE__*/React__default.createElement(Spinner, {
    size: "small",
    accessibilityLabel: accessibilityLabel
  }));
}
const cssVars = {
  multilineTruncate: '--osui_plain-action-multiline-truncate',
  fontWeight: '--osui_plain-action-font-weight'
};
function PlainAction({
  content,
  icon,
  disclosure = false,
  loading = false,
  id,
  accessibilityLabel,
  ariaControls,
  ariaExpanded,
  ariaDescribedBy,
  ariaLabelledBy,
  // Both `true/false` are explicit values, therefore `pressed` needs to default to `undefined`.
  pressed,
  disabled = false,
  skipFocus = false,
  slim = false,
  noPadding = false,
  outline = false,
  unstyled = false,
  vertical = false,
  alignLeft = false,
  fullWidth = false,
  lineHeight = 'base',
  fontWeight = 'regular',
  truncate = false,
  colorScheme = 'light',
  destructive = false,
  multilineTruncate,
  removeUnderline = false,
  url,
  external = false,
  renderActionAsUrl = false,
  download = false,
  submit = false,
  extraPadding = false,
  padding,
  withSurfaceSubduedBackground = false,
  background,
  onAction,
  role = getDefaultAriaRole({
    onAction,
    renderActionAsUrl
  }),
  prefix,
  subtitle,
  interactive = false,
  fontSize = 'bodyMd',
  small = false,
  tone,
  ...buttonProps
}) {
  const [i18n] = useI18n({
    id: 'PlainAction_lfoich',
    fallback: _en,
    translations(locale) {
      if (["cs", "da", "de", "es", "fi", "fr", "it", "ja", "ko", "nb", "nl", "pl", "pt-BR", "pt-PT", "sv", "th", "tr", "vi", "zh-CN", "zh-TW"].indexOf(locale) < 0) {
        return;
      }
      return import( /* webpackChunkName: "PlainAction_lfoich-i18n", webpackMode: "lazy-once" */`./translations/${locale}.json`).then(dict => dict && dict.default);
    }
  });
  const newWindowAccessibilityHint = i18n.translate('PlainAction.newWindowAccessibilityHint');
  const plainContentMarkup = getPlainContentMarkup({
    content,
    icon,
    disclosure,
    loading,
    prefix,
    subtitle,
    externalLabel: externalLabelCompatible({
      content,
      url,
      renderActionAsUrl,
      external,
      onAction
    }) ? newWindowAccessibilityHint : undefined
  });
  if (plainContentMarkup == null) {
    if (!IS_PROD_ENV) {
      console.warn(errorLabels.noProps);
    }
    return null;
  }
  const iconOnly = (icon || disclosure) && !content;
  const hasAccessibleLabel = Boolean(accessibilityLabel || ariaLabelledBy);
  if (!IS_PROD_ENV && iconOnly && !hasAccessibleLabel) {
    console.warn(errorLabels.accessibleLabel);
  }
  const isDisabled = disabled || loading;
  const backgroundStyle = background ? styles[variationName('background', background)] : null;
  const paddingStyle = padding ? styles[variationName('padding', padding)] : null;
  const fontSizeStyle = fontSize ? styles[variationName('fontSize', fontSize)] : null;
  const plainActionClassNames = classNames(styles.PlainAction, backgroundStyle, fontSizeStyle, paddingStyle, {
    [styles.destructive]: destructive,
    [styles.dark]: colorScheme === 'dark',
    [styles.darkInverse]: colorScheme === 'darkInverse',
    [styles.disclosure]: disclosure,
    [styles.hasContent]: Boolean(content || subtitle),
    [styles.loading]: loading,
    [styles.disabled]: isDisabled,
    [styles.pressed]: pressed,
    [styles.slim]: slim,
    [styles.noPadding]: noPadding,
    [styles.iconOnly]: iconOnly,
    [styles.outline]: outline,
    [styles.alignLeft]: alignLeft,
    [styles.fullWidth]: fullWidth,
    [styles.truncate]: truncate,
    [styles.multilineTruncate]: Boolean(!truncate && multilineTruncate),
    [styles.unstyled]: unstyled,
    [styles.hyperlink]: isLink({
      url,
      onAction,
      renderActionAsUrl
    }),
    [styles.removeUnderline]: removeUnderline,
    [styles.vertical]: verticalCompatible({
      content,
      icon,
      vertical,
      subtitle
    }),
    [styles.looseLineHeight]: lineHeight === 'loose',
    [styles.extraLooseLineHeight]: lineHeight === 'extraLoose',
    [styles.extraPadding]: extraPadding,
    [styles.withSurfaceSubduedBackground]: withSurfaceSubduedBackground,
    [styles.subtitle]: Boolean(subtitle),
    [styles.interactive]: interactive,
    [styles.small]: small,
    [styles.subtitleAndContent]: Boolean(subtitle && content),
    [styles.toneMagic]: tone === Tone.Magic
  });
  const multilineTruncateStyle = multilineTruncate ? {
    [cssVars.multilineTruncate]: Math.max(1, Math.floor(multilineTruncate))
  } : undefined;
  const style = {
    [cssVars.fontWeight]: `var(--p-font-weight-${fontWeight})`,
    ...multilineTruncateStyle
  };
  return /*#__PURE__*/React__default.createElement(UnstyledButton, Object.assign({
    className: plainActionClassNames,
    tabIndex: skipFocus || disabled ? -1 : undefined,
    id: id,
    role: role,
    accessibilityLabel: accessibilityLabel,
    ariaControls: ariaControls,
    ariaExpanded: ariaExpanded,
    ariaDescribedBy: ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    disabled: isDisabled,
    pressed: pressed,
    loading: loading,
    url: url,
    external: Boolean(url && external),
    download: download,
    submit: submit,
    onClick: onAction,
    style: style
  }, buttonProps), plainContentMarkup);
}
function getDisclosureIconSource(disclosure) {
  switch (disclosure) {
    case PlainActionDisclosureValue.Select:
      return SelectIcon;
    case PlainActionDisclosureValue.Up:
      return ChevronUpIcon;
    case PlainActionDisclosureValue.CaretDown:
      return CaretDownIcon;
    case PlainActionDisclosureValue.CaretUp:
      return CaretUpIcon;
    case PlainActionDisclosureValue.ChevronDown:
      return ChevronDownIcon;
    case PlainActionDisclosureValue.ChevronUp:
      return ChevronUpIcon;
    case PlainActionDisclosureValue.Down:
    default:
      return ChevronDownIcon;
  }
}
function getPlainContentMarkup({
  content,
  icon,
  disclosure,
  loading,
  externalLabel,
  prefix,
  subtitle
}) {
  const subtitleMarkup = subtitle && subtitle.trim().length > 0 ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.Subtitle
  }, subtitle) : null;
  const iconMarkup = icon || prefix ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.Prefix
  }, getPrefixContent({
    icon,
    prefix
  })) : null;
  const contentMarkup = content && content.trim().length > 0 ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.Content
  }, content) : null;
  const externalUrlMarkup = externalLabel ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.ExternalIcon
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.IconWrapper
  }, /*#__PURE__*/React__default.createElement(Icon, {
    source: ExternalSmallIcon,
    accessibilityLabel: externalLabel
  }))) : null;
  const wrappedContentMarkup = contentMarkup || externalUrlMarkup ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.WrappedContent
  }, contentMarkup, externalUrlMarkup) : null;
  const wrappedContentMarkupWithSubtitle = subtitleMarkup ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.SubtitleWrapperContent
  }, subtitleMarkup, wrappedContentMarkup) : wrappedContentMarkup;
  const disclosureMarkup = disclosure ? /*#__PURE__*/React__default.createElement("div", {
    className: styles.Disclosure
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.IconWrapper
  }, disclosure === PlainActionDisclosureValue.ChevronDownTiny ? /*#__PURE__*/React__default.createElement(ChevronDownTiny, null) : /*#__PURE__*/React__default.createElement(Icon, {
    source: getDisclosureIconSource(disclosure),
    tone: "subdued"
  }))) : null;
  const loadingMarkup = loading ? /*#__PURE__*/React__default.createElement(LoadingIndicator, null) : null;
  const interiorMarkup = iconMarkup || wrappedContentMarkupWithSubtitle || disclosureMarkup ? /*#__PURE__*/React__default.createElement("div", {
    className: classNames(styles.Interior, {
      [styles.InteriorWithSubtitle]: subtitleMarkup
    })
  }, iconMarkup, wrappedContentMarkupWithSubtitle, disclosureMarkup) : null;
  return interiorMarkup || loadingMarkup ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, interiorMarkup, loadingMarkup) : null;
}
function externalLabelCompatible({
  content,
  url,
  renderActionAsUrl,
  external,
  onAction
}) {
  return isLink({
    url,
    onAction,
    renderActionAsUrl
  }) && Boolean(content && external);
}
function verticalCompatible({
  content,
  icon,
  vertical,
  subtitle
}) {
  const hasContent = Boolean(content || subtitle);
  return Boolean(hasContent && icon && vertical);
}
function isLink({
  renderActionAsUrl,
  url,
  onAction
}) {
  return Boolean(url || renderActionAsUrl && onAction);
}
function getDefaultAriaRole({
  onAction,
  renderActionAsUrl
}) {
  if (onAction && renderActionAsUrl) {
    return AriaRole.Link;
  }
  return undefined;
}
function getPrefixContent({
  icon,
  prefix
}) {
  if (prefix) {
    return /*#__PURE__*/React__default.createElement("span", {
      className: styles.PrefixItem
    }, prefix);
  } else if (icon) {
    return /*#__PURE__*/React__default.createElement(Icon, {
      source: icon
    });
  }
  return null;
}

export { PlainAction, errorLabels };
